import React, { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { auth, db } from "../firebase";
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    getDoc,
    doc,
    updateDoc,
    Timestamp,
    getDocs,
} from "firebase/firestore";
import { useReactToPrint } from "react-to-print";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Typography,
    Select,
    MenuItem,
    FormControl,
    Card,
    CardContent,
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentsIcon from "@mui/icons-material/Payments";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

export default function ViewOrders() {
    /* ================= TOAST ================= */
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
    });

    /* ================= STATE ================= */
    const [propertyId, setPropertyId] = useState("");
    const [orders, setOrders] = useState([]);
    const [orderItems, setOrderItems] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [searchText, setSearchText] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [printDialogOpen, setPrintDialogOpen] = useState(false);
    const [paymentModes, setPaymentModes] = useState([]);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

    const [totalAmount, setTotalAmount] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [tip, setTip] = useState(0);
    const [paid, setPaid] = useState(0);
    const [payMode, setPayMode] = useState("");




    const printRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
    });

    /* ================= AUTH + PROPERTY ================= */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (user) => {
            if (!user) return;

            const snap = await getDoc(doc(db, "authroles", user.uid));
            if (!snap.exists()) {
                Toast.fire({ icon: "error", title: "Property not assigned" });
                return;
            }
            setPropertyId(snap.data().propertyId);
        });

        return () => unsub();
    }, []);

    useEffect(() => {
        if (!propertyId) return;

        const q = query(
            collection(db, "orders"),
            where("propertyId", "==", propertyId),
            where("status", "!=", 'CANCELLED'),
            orderBy("createdAt", "desc"),
            limit(200)
        );

        const unsub = onSnapshot(q, async (snap) => {
            const data = await Promise.all(
                snap.docs.map(async (d) => {
                    let custname = "";
                    let tableno = "";
                    let roomno = "";   // ✅ ADD
                    // CUSTOMER
                    if (d.data().customerid) {
                        const cs = await getDoc(
                            doc(db, "restocustomers", d.data().customerid)
                        );
                        custname = cs.exists() ? cs.data().name : "";
                    }

                    // TABLE / SEATING
                    if (d.data().tableid) {
                        const ts = await getDoc(
                            doc(db, "seatings", d.data().tableid)
                        );
                        tableno = ts.exists() ? ts.data().seatname : "";
                    }

                    // ROOM POSTING (fetch by orderId)
                    const rpSnap = await getDocs(
                        query(
                            collection(db, "roompostings"),
                            where("orderId", "==", d.id),
                            limit(1)
                        )
                    );

                    if (!rpSnap.empty) {
                        roomno = rpSnap.docs[0].data().roomno || "";
                    }

                    return {
                        id: d.id,
                        data: { ...d.data() },
                        custname,
                        tableno,
                        roomno,           // ✅ ADD THIS
                        staffname: d.data().waiterid || "",
                    };
                })
            );

            setOrders(data);
        });

        return () => unsub();
    }, [propertyId]);

    /* ================= OPEN EDIT ================= */
    const handleOpenEditDialog = (order) => {
        setSelectedOrder(order);
        setEditDialogOpen(true);

        const q = query(
            collection(db, "orderitems"),
            where("orderid", "==", order.id)
        );

        const unsub = onSnapshot(q, (snap) => {
            setOrderItems(
                snap.docs.map((d) => ({
                    id: d.id,
                    ...d.data(),
                }))
            );
        });

        return () => unsub();
    };

    /* ================= INLINE EDIT ================= */
    const handleItemChange = (id, field, value) => {
        setOrderItems((prev) =>
            prev.map((i) =>
                i.id === id
                    ? {
                        ...i,
                        [field]: value,
                        total:
                            field === "rate" || field === "quantity"
                                ? (field === "rate" ? value : i.rate) *
                                (field === "quantity" ? value : i.quantity)
                                : i.total,
                    }
                    : i
            )
        );
    };

    /* ================= SAVE ITEMS ================= */
    const saveItems = async () => {
        try {
            for (let item of orderItems) {
                await updateDoc(doc(db, "orderitems", item.id), {
                    rate: Number(item.rate),
                    quantity: Number(item.quantity),
                    total: Number(item.total),
                });
            }
            Toast.fire({ icon: "success", title: "Items updated" });
            setEditDialogOpen(false);
        } catch (e) {
            Toast.fire({ icon: "error", title: "Update failed" });
        }
    };

    /* ================= FILTER ================= */
    const filteredOrders = orders.filter((o) =>
        searchText
            ? o.data.billno?.toString().includes(searchText)
            : true
    );

    const paginatedOrders = filteredOrders.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );


    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };


    const grandTotal = totalAmount - discount + tip;
    const remaining = grandTotal - paid;

    const handleCancelOrder = async (order) => {
        const res = await Swal.fire({
            title: "Cancel Order?",
            text: "This action cannot be undone",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Cancel",
        });

        if (!res.isConfirmed) return;

        await updateDoc(doc(db, "orders", order.id), {
            status: "CANCELLED",
            cancelledAt: Timestamp.now(),
        });

        Toast.fire({ icon: "success", title: "Order cancelled" });
    };

    const openPaymentDialog = (order) => {
        setSelectedOrder(order);

        setTotalAmount(order.data.totalamount || 0);
        setDiscount(order.data.discount || 0);
        setTip(order.data.tip || 0);
        setPaid(0);
        setPayMode("");

        setPaymentDialogOpen(true);
    };


    useEffect(() => {
        if (!propertyId) return;

        const q = query(
            collection(db, "paymentmodes"),
            where("propertyId", "==", propertyId),
        );

        const unsub = onSnapshot(q, (snap) => {
            setPaymentModes(
                snap.docs.map((d) => ({
                    id: d.id,
                    ...d.data(),
                }))
            );
        });

        return () => unsub();
    }, [propertyId]);


    const savePayment = async () => {

        await updateDoc(doc(db, "orders", selectedOrder.id), {
            status: "BILLED",
            billdate: Timestamp.now(),
            totalamount: totalAmount,
            discount,
            tip,
            paid,
            remaining,

            paymentmode: payMode,
            updatedAt: Timestamp.now(),
        });

        Toast.fire({ icon: "success", title: "Payment saved" });
        setPaymentDialogOpen(false);
    };

    /* ================= UI ================= */
    return (
        <Box className="p-3">
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" mb={2}>
                        <Typography variant="h5">View Orders</Typography>
                        <Button variant="contained" href="table-orders">
                            Add Orders
                        </Button>
                    </Box>

                    <TextField
                        size="small"
                        placeholder="Search Bill No"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        sx={{ mb: 2, width: 250 }}
                    />

                    {/* ================= TABLE ================= */}
                    <TableContainer
                        component={Paper}
                        sx={{
                            borderRadius: 3,
                            boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                        }}
                    >

                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#abc7fe" }}>
                                    {[
                                        "#",
                                        "Date",
                                        "Table",
                                        "Bill No",
                                        "Customer",
                                        "Sub Total",
                                        "Discount",
                                        "Tip",
                                        "Grand Total",
                                        "Paid",
                                        "Balance",
                                        "Status",
                                        "Staff",
                                        "Action",
                                    ].map((h) => (
                                        <TableCell key={h} sx={{ fontWeight: "bold" }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginatedOrders.map((o, i) => {
                                    const subTotal = Number(o.data.totalamount || 0);
                                    const discountAmount = Number(o.data.discountAmount || 0);
                                    const discountValue = o.data.discountValue || 0;
                                    const discountType = o.data.discountType || "amount";
                                    const tip = Number(o.data.tip || 0);

                                    const grandTotal = Number(
                                        o.data.grandTotal || subTotal - discountAmount + tip
                                    );

                                    const paid = Number(o.data.paid || 0);
                                    const balance = grandTotal - paid;

                                    let paymentStatus = "UNPAID";
                                    if (balance <= 0 && paid > 0) paymentStatus = "PAID";
                                    else if (paid > 0 && balance > 0) paymentStatus = "PARTIAL";

                                    return (
                                        <TableRow key={o.id} hover>
                                            <TableCell>
                                                {page * rowsPerPage + i + 1}
                                            </TableCell>

                                            <TableCell>
                                                {o.data.billdate
                                                    ? moment(o.data.billdate.toDate()).format("DD-MM-YYYY")
                                                    : "-"}
                                            </TableCell>

                                            <TableCell>
                                                <Typography fontWeight="bold" color="primary">
                                                    {o.roomno
                                                        ? `Room ${o.roomno}`
                                                        : o.tableno || "-"}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>{o.data.billno || "-"}</TableCell>

                                            <TableCell>{o.custname || "-"}</TableCell>

                                            {/* Subtotal */}
                                            <TableCell>
                                                ₹{subTotal.toFixed(2)}
                                            </TableCell>

                                            {/* Discount */}
                                            <TableCell>
                                                {discountType === "percent"
                                                    ? `${discountValue}%`
                                                    : `₹${discountAmount.toFixed(2)}`}
                                            </TableCell>

                                            {/* Tip */}
                                            <TableCell>
                                                ₹{tip.toFixed(2)}
                                            </TableCell>

                                            {/* Grand Total */}
                                            <TableCell sx={{ fontWeight: "bold" }}>
                                                ₹{grandTotal.toFixed(2)}
                                            </TableCell>

                                            {/* Paid */}
                                            <TableCell>
                                                ₹{paid.toFixed(2)}
                                            </TableCell>

                                            {/* Balance */}
                                            <TableCell>
                                                <Typography
                                                    fontWeight="bold"
                                                    color={balance <= 0 ? "green" : "error"}
                                                >
                                                    ₹{balance.toFixed(2)}
                                                </Typography>
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell>
                                                <Typography
                                                    fontWeight="bold"
                                                    color={
                                                        paymentStatus === "PAID"
                                                            ? "green"
                                                            : paymentStatus === "PARTIAL"
                                                                ? "orange"
                                                                : "error"
                                                    }
                                                >
                                                    {paymentStatus}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>{o.staffname || "-"}</TableCell>

                                            <TableCell>
                                                <Tooltip title="Cancel Order">
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleCancelOrder(o)}
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={filteredOrders.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{
                            borderTop: "1px solid #eee",
                            backgroundColor: "#f9fafb",
                        }}
                    />


                    {/* ================= EDIT DIALOG ================= */}
                    <Dialog open={editDialogOpen} maxWidth="lg" fullWidth>
                        <DialogTitle>Edit Order Items</DialogTitle>
                        <DialogContent>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell>Rate</TableCell>
                                        <TableCell>Qty</TableCell>
                                        <TableCell>Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {orderItems.map((i) => (
                                        <TableRow key={i.id}>
                                            <TableCell>{i.name}</TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={i.rate}
                                                    onChange={(e) =>
                                                        handleItemChange(i.id, "rate", e.target.value)
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={i.qty}
                                                    onChange={(e) =>
                                                        handleItemChange(
                                                            i.id,
                                                            "quantity",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>{i.qty * i.rate}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={saveItems} variant="contained" color="success">
                                Save
                            </Button>
                            <Button
                                onClick={() => setEditDialogOpen(false)}
                                variant="outlined"
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* ================= PRINT ================= */}
                    <Dialog open={printDialogOpen} maxWidth="md" fullWidth>
                        <DialogTitle>Print Bill</DialogTitle>
                        <DialogContent>
                            <Box ref={printRef} p={2}>
                                <Typography align="center" variant="h6">
                                    PRATIK BAR & RESTO
                                </Typography>

                                <Typography>
                                    Bill No: {selectedOrder?.data.billno}
                                </Typography>

                                <Table sx={{ mt: 2 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell>Rate</TableCell>
                                            <TableCell>Qty</TableCell>
                                            <TableCell>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {orderItems.map((i) => (
                                            <TableRow key={i.id}>
                                                <TableCell>{i.itemname}</TableCell>
                                                <TableCell>{i.rate}</TableCell>
                                                <TableCell>{i.quantity}</TableCell>
                                                <TableCell>{i.total}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handlePrint} variant="contained">
                                Print
                            </Button>
                            <Button
                                onClick={() => setPrintDialogOpen(false)}
                                variant="outlined"
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>
                </CardContent>
            </Card>


            <Dialog open={paymentDialogOpen} maxWidth="sm" fullWidth>
                <DialogTitle>Make Payment</DialogTitle>

                <DialogContent>
                    <Box display="grid" gap={2} mt={1}>
                        <TextField
                            size="small"
                            label="Total"
                            value={totalAmount}
                            InputProps={{ readOnly: true }}
                        />

                        <TextField
                            size="small"
                            label="Discount"
                            type="number"
                            value={discount}
                            onChange={(e) => setDiscount(Number(e.target.value))}
                        />

                        <TextField
                            size="small"
                            label="Tip"
                            type="number"
                            value={tip}
                            onChange={(e) => setTip(Number(e.target.value))}
                        />

                        <TextField
                            size="small"
                            label="Paid Amount"
                            type="number"
                            value={paid}
                            onChange={(e) => setPaid(Number(e.target.value))}
                        />

                        <TextField
                            size="small"
                            label="Remaining"
                            value={remaining}
                            InputProps={{ readOnly: true }}
                        />

                        <FormControl fullWidth size="small">
                            <TextField
                                select
                                label="Payment Mode"
                                value={payMode}
                                onChange={(e) => setPayMode(e.target.value)}
                            >
                                <MenuItem value="">Select Paymode</MenuItem>

                                {paymentModes.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.paymode}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </FormControl>

                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={savePayment}
                        disabled={!payMode || paid <= 0}
                    >
                        Save Payment
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setPaymentDialogOpen(false)}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>



        </Box>
    );
}
